import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useCompleteClientPickup } from '@/models/article';
import { useInvoices } from '@/models/invoice';
import { ArticleWithRelations, RequestWithRelations, useRequestDocuments } from '@/models/request';
import { hasPermissionOnAnyScope } from '@/models/user';
import { useCurrentSession } from '@/services/auth';

import { WorkshopUploadInvoice } from '../ArticleInfo/ArticleWorkshopPayment';

import { PickUpReceiptDrawer } from './shared/PickUpReceiptDrawer';

export const CompleteClientPickup = ({
  request,
  article,
  onActionDone,
}: {
  request: RequestWithRelations;
  article: ArticleWithRelations;
  onActionDone: () => void;
}) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();

  const [isUploadInvoiceReminderModalOpen, setIsUploadInvoiceReminderModalOpen] = useState(false);
  const [isPickUpReceiptDrawerOpen, setIsPickUpReceiptDrawerOpen] = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    mutateAsync: completeClientPickup,
    isPending: isPendingCompleteClientPickup,
    isSuccess: isSuccessCompleteClientPickup,
  } = useCompleteClientPickup({
    articleId: article.id,
  });

  const handleCompletePickUp = async () => {
    try {
      await completeClientPickup();
      setIsUploadInvoiceReminderModalOpen(false);
      setIsPickUpReceiptDrawerOpen(false);
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const isLoadingCompletePickUp = isPendingCompleteClientPickup || isSuccessCompleteClientPickup;

  const hasInvoicePermission = hasPermissionOnAnyScope(
    currentSession,
    'view_invoice',
    request.organizationId
  );

  const { data: { invoices } = { invoices: [] }, isLoading: isLoadingInvoices } = useInvoices(
    {
      articleId: article.id,
      originCompany: article.workshop?.companyId ?? undefined,
    },
    {
      enabled: !!hasInvoicePermission && !!article.workshop?.companyId,
    }
  );

  const shouldShowUploadInvoiceReminder =
    !!article.repairedAt &&
    !!article.workshop?.workshopConfig?.nonDigitalized &&
    !isLoadingInvoices &&
    !invoices.length &&
    article.workshopPaymentStrategy === 'store-cash-out';

  const { data: generatedDocuments = [] } = useRequestDocuments({
    requestId: request.id,
  });

  const alreadySignedPickUpReceipt =
    request.articles.length === 1
      ? generatedDocuments.find((document) => document.type === 'pick-up-receipt')
      : generatedDocuments.find(
          (document) => document.type === 'pick-up-receipt' && document.articleId === article.id
        );

  const shouldSignPickUpReceipt =
    article.step?.step === 'transit' &&
    article.step.config.clientPickUpSignature?.enabled &&
    !alreadySignedPickUpReceipt;

  return (
    <>
      <Footer>
        {!!apiError && !isUploadInvoiceReminderModalOpen && !isPickUpReceiptDrawerOpen && (
          <FooterError>{apiError}</FooterError>
        )}
        <FooterButtons>
          <Button
            variant="primary"
            onPress={() => {
              if (shouldShowUploadInvoiceReminder) {
                setIsUploadInvoiceReminderModalOpen(true);
              } else if (shouldSignPickUpReceipt) {
                setIsPickUpReceiptDrawerOpen(true);
              } else {
                handleCompletePickUp();
              }
            }}
            isLoading={
              isLoadingCompletePickUp &&
              !isUploadInvoiceReminderModalOpen &&
              !isPickUpReceiptDrawerOpen
            }
          >
            {shouldSignPickUpReceipt ? (
              <Trans id="article.actions.complete_client_pickup.label.signature">
                Proceed to pickup signature
              </Trans>
            ) : (
              <Trans id="article.actions.complete_client_pickup.label">Client pickup done</Trans>
            )}
          </Button>
          <UploadInvoiceReminderModal
            isOpen={isUploadInvoiceReminderModalOpen}
            onClose={() => setIsUploadInvoiceReminderModalOpen(false)}
            onCompletePickUp={() => {
              if (shouldSignPickUpReceipt) {
                setIsUploadInvoiceReminderModalOpen(false);
                setIsPickUpReceiptDrawerOpen(true);
              } else {
                handleCompletePickUp();
              }
            }}
            isLoadingCompletePickUp={isLoadingCompletePickUp}
            completePickUpError={apiError}
          />
        </FooterButtons>
      </Footer>
      <PickUpReceiptDrawer
        isOpen={isPickUpReceiptDrawerOpen}
        setIsOpen={setIsPickUpReceiptDrawerOpen}
        request={request}
        article={article}
        onCompletePickUp={handleCompletePickUp}
        isLoadingCompletePickUp={isLoadingCompletePickUp}
        completePickUpError={apiError}
      />
    </>
  );
};

const UploadInvoiceReminderModal = ({
  isOpen,
  onClose,
  onCompletePickUp,
  isLoadingCompletePickUp,
  completePickUpError,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCompletePickUp: () => void;
  isLoadingCompletePickUp: boolean;
  completePickUpError: string;
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onClose}
      title={
        <Trans id="article.actions.complete_client_pickup.upload_invoice_reminder.title">
          Invoice missing
        </Trans>
      }
    >
      <main>
        <Paragraph>
          <Trans id="article.actions.complete_client_pickup.upload_invoice_reminder.text">
            You haven't uploaded the workshop's invoice yet. You can do it now before completing
            this step, or if you prefer, you can do it later from the item page or the accounting
            tab.
          </Trans>
        </Paragraph>
      </main>
      <Footer>
        {!!completePickUpError && <FooterError>{completePickUpError}</FooterError>}
        <FooterButtons>
          <Button
            variant="secondary"
            size="medium"
            onPress={onCompletePickUp}
            isLoading={isLoadingCompletePickUp}
          >
            <Trans id="article.actions.complete_client_pickup.upload_invoice_reminder.later">
              I'll do it later
            </Trans>
          </Button>
          <WorkshopUploadInvoice variant="primary" />
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
